/**
 * Engraving state the screens share: which plate designs are on offer, and
 * which moulds' engrave files have already been downloaded.
 *
 * Both come from the server and are online-only, so - like mould locations -
 * each is cached on the device and served from there without a connection.
 */
import { useEffect, useState } from 'react';
import { api, isNetworkError } from '../../api/client.js';
import { DESIGN_B_CODE } from './NameplateCompareDialog.js';

export interface OfferedDesign {
  code: string;
  name: string;
  summary: string;
  templateId: string | null;
}

/** What is offered when the server cannot be asked: Design B, the client's design. */
const FALLBACK_DESIGNS: OfferedDesign[] = [
  { code: DESIGN_B_CODE, name: 'Design B', summary: 'Jay Precision logo, the five details', templateId: null },
];

const DESIGNS_KEY = 'mt_offered_designs';
const DOWNLOADS_KEY = 'mt_engrave_downloads';
/** Fired after an engrave file is downloaded here, so every tick updates at once. */
const DOWNLOADED_EVENT = 'mt:engrave-downloaded';

function readCache<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}
function writeCache(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage full or blocked: the in-memory copy still works.
  }
}

/**
 * The plate designs the server offers (only Design B unless NAMEPLATE_DESIGNS
 * on the server adds others). A design that is not offered is not shown
 * anywhere - not in the bulk dialog, not on the mould page.
 */
export function useOfferedDesigns(): OfferedDesign[] {
  const [designs, setDesigns] = useState<OfferedDesign[]>(() => {
    const cached = readCache<OfferedDesign[]>(DESIGNS_KEY, []);
    return Array.isArray(cached) && cached.length ? cached : FALLBACK_DESIGNS;
  });
  useEffect(() => {
    let cancelled = false;
    if (!navigator.onLine) return;
    void api
      .listNameplateDesigns()
      .then((r) => {
        if (cancelled || !r.data?.length) return;
        const next = r.data.map((d) => ({ code: d.code, name: d.name, summary: d.summary, templateId: d.templateId }));
        setDesigns(next);
        writeCache(DESIGNS_KEY, next);
      })
      .catch((e: unknown) => {
        if (!isNetworkError(e)) console.warn('plate designs unavailable', e);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return designs;
}

/** One mould's engrave file history, as the tick shows it. */
export interface EngraveDownload {
  count: number;
  lastAt: string;
  lastBy: string | null;
  lastSource: string;
}

/** mouldId -> its download history; moulds never downloaded are absent. */
export type EngraveDownloads = Record<string, EngraveDownload>;

/** Tell every screen an engrave file was just downloaded, so its tick appears at once. */
export function notifyEngraveDownloaded(): void {
  window.dispatchEvent(new Event(DOWNLOADED_EVENT));
}

export function useEngraveDownloads(): EngraveDownloads {
  const [downloads, setDownloads] = useState<EngraveDownloads>(() => readCache<EngraveDownloads>(DOWNLOADS_KEY, {}));

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      if (!navigator.onLine) return;
      void api
        .listEngraveDownloads()
        .then((r) => {
          if (cancelled) return;
          const next: EngraveDownloads = {};
          for (const d of r.data) next[d.mouldId] = { count: d.count, lastAt: d.lastAt, lastBy: d.lastBy, lastSource: d.lastSource };
          setDownloads(next);
          writeCache(DOWNLOADS_KEY, next);
        })
        .catch((e: unknown) => {
          if (!isNetworkError(e)) console.warn('engrave downloads unavailable', e);
        });
    };
    load();
    window.addEventListener(DOWNLOADED_EVENT, load);
    window.addEventListener('online', load);
    return () => {
      cancelled = true;
      window.removeEventListener(DOWNLOADED_EVENT, load);
      window.removeEventListener('online', load);
    };
  }, []);

  return downloads;
}

/** "16 Oct 2026" - the date a file was last downloaded. */
export function downloadDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** A sentence for a tooltip: when, by whom, how often. */
export function describeDownload(d: EngraveDownload): string {
  const when = new Date(d.lastAt).toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  const who = d.lastBy ? ` by ${d.lastBy}` : '';
  const how = d.lastSource === 'batch' ? ' (in a bulk ZIP)' : '';
  return `Engrave file last downloaded ${when}${who}${how}. Downloaded ${d.count} time${d.count === 1 ? '' : 's'} in all.`;
}
